import * as React from 'react'
import {graphql, Link} from 'gatsby'
import {withPrismicPreview} from 'gatsby-plugin-prismic-previews'
import * as prismicH from '@prismicio/helpers'
import {useTheme} from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FadeInWrapper from 'src/components/atoms/FadeInWrapper'
import RenderRichText from 'src/utils/renderRichText'
import CompanyTitleContrast from 'src/components/atoms/CompanyTitleContrast'
import useSlice from 'src/hooks/useSlice'

import type {AllDocumentTypes, HomeDocument} from '../../types.generated'
import HomePage from 'src/components/templates/HomePage'
import {
  Certificate,
  CPU,
  Idea,
  Leadership,
  Salary,
  Search,
  Shield,
  Storm,
  Telescope,
} from 'src/assets/icons'

const ICONS = {
  data: [
    {
      icon: (
        <FadeInWrapper>
          <Leadership />
        </FadeInWrapper>
      ),
      title: 'independent',
    },
    {
      icon: (
        <FadeInWrapper delay={200}>
          <Shield />
        </FadeInWrapper>
      ),
      title: 'reliable',
    },
    {
      icon: (
        <FadeInWrapper>
          <Salary />
        </FadeInWrapper>
      ),
      title: 'accessible',
    },
  ],
  analytics: [
    {
      icon: (
        <FadeInWrapper>
          <Idea />
        </FadeInWrapper>
      ),
      title: 'illuminating',
    },
    {
      icon: (
        <FadeInWrapper delay={200}>
          <Storm />
        </FadeInWrapper>
      ),
      title: 'powerful',
    },
    {
      icon: (
        <FadeInWrapper>
          <Telescope />
        </FadeInWrapper>
      ),
      title: 'forward looking',
    },
  ],
  solutions: [
    {
      icon: (
        <FadeInWrapper>
          <Certificate />
        </FadeInWrapper>
      ),
      title: 'trusted',
    },
    {
      icon: (
        <FadeInWrapper delay={200}>
          <CPU />
        </FadeInWrapper>
      ),
      title: 'tailored',
    },
    {
      icon: (
        <FadeInWrapper>
          <Search />
        </FadeInWrapper>
      ),
      title: 'perceptive',
    },
  ],
}

interface Props {
  data: {
    prismicHome: HomeDocument
    prismicBenchmarkBox: AllDocumentTypes
  }
}

const IndexPage = ({data}: Props) => {
  const hero = useSlice(data.prismicHome, 'hero')
  const dataProduct = useSlice(data.prismicHome, 'product')[0]
  const analytcisProduct = useSlice(data.prismicHome, 'product')[1]
  const solutionsProduct = useSlice(data.prismicHome, 'product')[2]
  const partners = useSlice(data.prismicHome, 'partners_block')
  const benchmark = useSlice(data.prismicBenchmarkBox)
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <HomePage
      logos={partners.items}
      icons={ICONS}
      heroContent={() => (
        <>
          <RenderRichText
            field={hero.primary.hero_title}
            components={{
              heading1: ({children}) => (
                <Typography variant="h3" component="h1" gutterBottom>
                  {children}
                </Typography>
              ),
            }}
          />
          <Box sx={{mb: 4}}>
            <RenderRichText field={hero.primary.hero_text} />
          </Box>
          <Button
            fullWidth={matches}
            variant="contained"
            component={Link}
            to="/register"
          >
            Register for a free trial
          </Button>
        </>
      )}
      elevatedCardContent={() => (
        <>
          <Box sx={{'& h6': {fontWeight: 'medium'}}}>
            <RenderRichText field={benchmark.title} />
          </Box>
          <RenderRichText field={benchmark.body_text} />
        </>
      )}
      dataContent={() => (
        <>
          <RenderRichText field={dataProduct.primary.product_name} />
          <RenderRichText field={dataProduct.primary.intro_text} />
          <RenderRichText field={dataProduct.primary.body_text} />
        </>
      )}
      analyticsContent={() => (
        <>
          <RenderRichText field={analytcisProduct.primary.product_name} />
          <RenderRichText field={analytcisProduct.primary.intro_text} />
          <RenderRichText field={analytcisProduct.primary.body_text} />
        </>
      )}
      solutionsContent={() => (
        <>
          <RenderRichText field={solutionsProduct.primary.product_name} />
          <RenderRichText field={solutionsProduct.primary.intro_text} />
          <RenderRichText field={solutionsProduct.primary.body_text} />
        </>
      )}
      logoSectionContent={() => (
        <>
          <CompanyTitleContrast
            title={prismicH.asText(partners.primary.title)}
          />
          <RenderRichText
            field={partners.primary.body_text}
            components={{
              paragraph: ({children}) => (
                <Typography variant="body1" align="center" gutterBottom>
                  {children}
                </Typography>
              ),
            }}
          />
        </>
      )}
    />
  )
}

export const query = graphql`
  {
    prismicBenchmarkBox {
      data {
        title {
          richText
        }
        body_text {
          richText
        }
      }
    }
    prismicHome {
      _previewable
      data {
        body {
          ... on PrismicSliceType {
            slice_type
          }
          ... on PrismicHomeDataBodyHero {
            slice_type
            primary {
              cta {
                url
              }
              hero_text {
                richText
              }
              hero_title {
                richText
              }
            }
          }
          ... on PrismicHomeDataBodyBenchmarkBox {
            slice_type
            primary {
              body_text {
                richText
              }
              intro_text {
                richText
              }
            }
          }
          ... on PrismicHomeDataBodyPartnersBlock {
            slice_type
            items {
              logo {
                url
                alt
              }
              title {
                text
              }
            }
            primary {
              body_text {
                richText
              }
              title {
                text
              }
            }
          }
          ... on PrismicHomeDataBodyProduct {
            slice_type
            primary {
              body_text {
                richText
              }
              intro_text {
                richText
              }
              product_name {
                richText
              }
            }
          }
        }
      }
    }
  }
`

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default withPrismicPreview(IndexPage)
